import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CustomerLayout } from "@/components/Layout/CustomerLayout";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { MODULE_ICONS } from "@/lib/serviceModules";
import { ChevronLeft, Bell, CheckCircle2, Sparkles, Loader2 } from "lucide-react";
import ProviderList from "./ProviderList";

// Per-module imagery + feature previews
const MODULE_DETAILS = {
  home_services: {
    hero: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1600&q=80",
    tagline_en: "Trusted hands, one tap away.",
    tagline_ar: "أيادٍ موثوقة بضغطة واحدة.",
    features_en: ["Plumbers, electricians, AC technicians", "Verified ID & certifications", "Same-day & scheduled booking", "Photo proof of work completed"],
    features_ar: ["سباكون، كهربائيون، فنيو تكييف", "هويات وشهادات معتمدة", "حجز فوري أو مجدول", "إثبات بالصور عند الانتهاء"],
  },
  courier: {
    hero: "https://images.unsplash.com/photo-1568667256549-094345857637?w=1600&q=80",
    tagline_en: "Send anything, anywhere in the city.",
    tagline_ar: "أرسل أي شيء، في أي مكان بالمدينة.",
    features_en: ["Same-day package delivery", "Document & parcel courier", "Live tracking & signature proof", "Scheduled pickups"],
    features_ar: ["توصيل الطرود في نفس اليوم", "نقل المستندات والطرود", "تتبع مباشر وإثبات التوقيع", "استلام مجدول"],
  },
  pharmacy: {
    hero: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=1600&q=80",
    tagline_en: "Medicines delivered, fast and safely.",
    tagline_ar: "الأدوية توصل بسرعة وأمان.",
    features_en: ["Prescription upload", "24/7 pharmacy access", "Cold-chain medicines", "Refill reminders"],
    features_ar: ["رفع الوصفات الطبية", "وصول للصيدليات 24/7", "أدوية مبردة بسلسلة باردة", "تذكيرات لإعادة الصرف"],
  },
  grocery: {
    hero: "https://images.unsplash.com/photo-1542838132-92c53300491e?w=1600&q=80",
    tagline_en: "Fresh groceries, when you need them.",
    tagline_ar: "بقالة طازجة، عندما تحتاجها.",
    features_en: ["Fruits, vegetables, dairy & more", "30-minute express delivery", "Recurring orders", "Quality guarantee"],
    features_ar: ["فواكه وخضروات وألبان والمزيد", "توصيل سريع خلال 30 دقيقة", "طلبات متكررة", "ضمان الجودة"],
  },
  hotels: {
    hero: "https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=1600&q=80",
    tagline_en: "Book hotels across Sudan & beyond.",
    tagline_ar: "احجز فنادق في السودان وخارجه.",
    features_en: ["From boutique to 5-star", "Best price guarantee", "Free cancellation options", "ServeOn loyalty points on every stay"],
    features_ar: ["من البوتيك إلى 5 نجوم", "ضمان أفضل سعر", "خيارات إلغاء مجانية", "نقاط ولاء سيرف أون عن كل إقامة"],
  },
  flats: {
    hero: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1600&q=80",
    tagline_en: "Find your perfect stay or long-term home.",
    tagline_ar: "اعثر على إقامتك المثالية أو منزل طويل الأمد.",
    features_en: ["Short & long-term rentals", "Verified property owners", "Photos & 360° tours", "Direct in-app chat with landlords"],
    features_ar: ["إيجارات قصيرة وطويلة المدى", "ملاك معتمدون", "صور وجولات 360°", "محادثة مباشرة مع المالك"],
  },
  business: {
    hero: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1600&q=80",
    tagline_en: "B2B services & marketplace.",
    tagline_ar: "خدمات وسوق B2B.",
    features_en: ["Accounting, legal, marketing", "Verified business providers", "Project-based or retainer", "Enterprise-grade SLAs"],
    features_ar: ["محاسبة وقانون وتسويق", "مزودون معتمدون", "حسب المشروع أو الاحتفاظ", "اتفاقيات SLA على مستوى المؤسسات"],
  },
};

export default function ServiceModulePage() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const { user } = useAuth();
  const [module, setModule] = useState(null);
  const [count, setCount] = useState(0);
  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [joined, setJoined] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/service-modules").then((r) => {
      const m = r.data.find((x) => x.key === key);
      setModule(m);
    });
    api.get("/waitlist/counts").then((r) => setCount(r.data[key] || 0));
  }, [key]);

  // Redirect for restaurants (which has a real module)
  useEffect(() => {
    if (module && module.key === "restaurants") navigate("/app", { replace: true });
  }, [module, navigate]);

  // When module is LIVE, render the generic provider list instead of the waitlist UI
  if (module && module.status === "live" && module.key !== "restaurants") {
    return <ProviderList />;
  }

  const join = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      await api.post("/waitlist", { module_key: key, email, name, phone });
      setJoined(true);
      const c = await api.get("/waitlist/counts");
      setCount(c.data[key] || 0);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to join waitlist");
    } finally { setLoading(false); }
  };

  if (!module) return <CustomerLayout><div className="p-8">Loading...</div></CustomerLayout>;

  const Icon = MODULE_ICONS[key] || Sparkles;
  const detail = MODULE_DETAILS[key] || { hero: module.image_url, tagline_en: module.description_en, tagline_ar: module.description_ar, features_en: [], features_ar: [] };
  const label = lang === "ar" ? module.label_ar : module.label_en;
  const tagline = lang === "ar" ? detail.tagline_ar : detail.tagline_en;
  const features = lang === "ar" ? detail.features_ar : detail.features_en;
  const accent = module.accent;

  return (
    <CustomerLayout>
      <div className="relative">
        <div className="h-56 md:h-72 overflow-hidden relative">
          <img src={detail.hero} alt={label} className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${accent}20 0%, ${accent}cc 100%)` }} />
          <button onClick={() => navigate("/app")} className="absolute top-4 start-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-gray-900" data-testid="back-to-home">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-3">
              <Icon className="w-8 h-8" />
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">{label}</h1>
            <p className="mt-2 text-white/90 max-w-xl">{tagline}</p>
            <div className="mt-4 inline-flex items-center gap-2 bg-white/15 backdrop-blur border border-white/25 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-300 so-pulse" />
              {module.status === "live" ? "Activating" : "Coming Soon"}
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-8">
          {/* Features */}
          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            {features.map((f) => (
              <div key={f} className="so-card p-4 flex gap-3 items-start">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${accent}1A`, color: accent }}>
                  <CheckCircle2 className="w-4 h-4" />
                </div>
                <div className="text-sm font-medium">{f}</div>
              </div>
            ))}
          </div>

          {/* Waitlist form */}
          {!joined ? (
            <form onSubmit={join} className="so-card p-6" data-testid="waitlist-form" style={{ borderColor: `${accent}40` }}>
              <div className="flex items-center gap-2 mb-3">
                <Bell className="w-5 h-5" style={{ color: accent }} />
                <div className="font-bold text-lg">{lang === "ar" ? "أعلمني عند الإطلاق" : "Notify me when it's live"}</div>
              </div>
              {count > 0 && (
                <div className="text-sm mb-4" style={{ color: accent }} data-testid="waitlist-count">
                  <b>{count.toLocaleString()}</b> {lang === "ar" ? "شخص في قائمة الانتظار" : "people already waiting"}
                </div>
              )}
              <div className="space-y-3">
                <input className="so-input" placeholder={lang === "ar" ? "اسمك" : "Your name"} value={name} onChange={e => setName(e.target.value)} data-testid="waitlist-name" />
                <input className="so-input" type="email" placeholder={lang === "ar" ? "البريد الإلكتروني" : "Email"} value={email} onChange={e => setEmail(e.target.value)} required data-testid="waitlist-email" />
                <input className="so-input" placeholder={lang === "ar" ? "رقم الهاتف (اختياري)" : "Phone (optional)"} value={phone} onChange={e => setPhone(e.target.value)} data-testid="waitlist-phone" />
              </div>
              {err && <div className="mt-3 text-sm text-red-500" data-testid="waitlist-error">{err}</div>}
              <button type="submit" disabled={loading} data-testid="waitlist-submit" className="w-full justify-center mt-4 inline-flex items-center gap-2 font-semibold px-6 py-3 rounded-xl text-white transition-all hover:brightness-110 disabled:opacity-50" style={{ background: accent }}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "ar" ? "انضم لقائمة الانتظار" : "Join the waitlist")}
              </button>
            </form>
          ) : (
            <div className="so-card p-8 text-center" data-testid="waitlist-success" style={{ borderColor: `${accent}80`, background: `${accent}08` }}>
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3" style={{ background: `${accent}1A`, color: accent }}>
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <div className="text-xl font-bold">{lang === "ar" ? "أنت في القائمة!" : "You're on the list!"}</div>
              <div className="text-sm text-[var(--so-text-soft)] mt-2 max-w-sm mx-auto">
                {lang === "ar"
                  ? "سنرسل لك إشعارًا فور إطلاق هذه الخدمة. شكرًا لانضمامك إلى رحلة سيرف أون."
                  : "We'll notify you the moment this service goes live. Thanks for joining the ServeOn journey."}
              </div>
              {count > 0 && (
                <div className="mt-4 text-sm font-bold" style={{ color: accent }}>
                  {count.toLocaleString()} {lang === "ar" ? "في الانتظار حتى الآن" : "waiting so far"}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </CustomerLayout>
  );
}
